import { useCallback, useEffect, useRef } from 'react'

import { useNavigate } from 'react-router-dom'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import {
  getPathByPageId,
  getContentAccessFlags,
} from '@sportworld/js-sdk/src/utils/utils'

import { getPagePopupById } from '@/actions'
import {
  analyticsVideoTypes,
  appOpenedSource,
  eventAction,
  eventObject,
  videoPlayerNames,
} from '@/constants/analytics'
import { LineTileShape } from '@/constants/common'
import { paths } from '@/constants/paths'
import { popupStatus } from '@/constants/popup'
import { openEventVideo } from '@/containers/EventDetailPage/Buttons/openEventVideo'
import { useEventVideoSources } from '@/hooks/useEventVideoSources'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { deepLinkToAppId } from '@/platform/platform'
import useConfirmationStore from '@/store/useConfirmationStore'
import useStore from '@/store/useStore'
import { useTileStatusStore } from '@/store/useTileStatusStore'
import {
  eventTypes,
  ListPopupType,
  MediaContentType,
  tileTypes,
} from '@/types/servicesTypes/configTypes'
import { SearchQuery } from '@/types/servicesTypes/searchTypes'
import { SlideDataType } from '@/types/sliderTypes'
import { sendAnalyticsEvent, sendPageViewEvent } from '@/utils/analyticsUtils'
import { checkIsEventPlayable } from '@/utils/eventUtils'
import {
  sendEventToGTAG,
  sendPageImpressionToGTAG,
  showError,
  showInfo,
} from '@/utils/utils'

export type OpenTileProps = {
  tileData: SlideDataType
  focusId: string
  isOpenedFromList?: boolean
  tileIndex?: number
  anchorId?: string
  lineId?: string
}

export function useOpenTile(options?: {
  shouldNavigateToEventUrl?: boolean
  listContextId?: string
  isSearchListType?: boolean
  name?: string
  tileShape?: LineTileShape
  searchResultType?: SearchQuery
  openWatchableEventPage?: boolean
}) {
  const {
    shouldNavigateToEventUrl,
    listContextId,
    isSearchListType = false,
    name,
    tileShape,
    searchResultType,
    openWatchableEventPage,
  } = options || {}

  const { t } = useCommonTranslation()
  const navigate = useNavigate()
  const controller = useRef<Nullable<AbortController>>()
  const { pagePopupStatus } = useStore((state) => state.popup)
  const setTileIsLoading = useTileStatusStore((state) => state.setTileIsLoading)
  const { isRegisteredUser } = useAuth()
  const { getEventVideoSources } = useEventVideoSources()

  useEffect(() => {
    if (pagePopupStatus === popupStatus.CLOSED) {
      controller.current?.abort()
    }
  }, [pagePopupStatus])

  return useCallback(
    async ({
      tileData,
      focusId,
      isOpenedFromList,
      tileIndex,
      anchorId,
      lineId,
    }: OpenTileProps) => {
      const state = useStore.getState()
      const { page } = state.page
      const { setCurrentEvent, setMediaVideo, setListPopup, setSubMenuType } =
        state.common

      const { pagePopup, closePagePopup } = state.popup

      const listOptions: ListPopupType | undefined =
        isOpenedFromList && listContextId
          ? {
              listContextId,
              isSearchListType,
              activeTileId: tileData.id,
              activeTileIndex: tileIndex,
              name,
              tileShape,
              searchResultType,
            }
          : undefined

      try {
        setTileIsLoading(focusId, true)

        if (tileData?.type === tileTypes.EVENT) {
          const event = tileData.data?.event

          const { isAccessible } = getContentAccessFlags(
            event,
            null,
            isRegisteredUser,
          )
          const isPlayable = checkIsEventPlayable(event)

          if (isPlayable && isAccessible && !openWatchableEventPage) {
            const isYouTube = event.type === eventTypes.YOUTUBE
            let eventVideoSources = null
            if (!isYouTube) {
              try {
                eventVideoSources = await getEventVideoSources(
                  event,
                  isRegisteredUser,
                )
              } catch (e) {}
            }
            if (isYouTube || eventVideoSources) {
              openEventVideo(event, eventVideoSources, listOptions)
              return
            }
          }

          const eventData = tileData?.data?.event && {
            ...tileData?.data?.event,
            listOptions,
          }
          setCurrentEvent(eventData || null)
          isDevMode && console.log('setCurrentEvent', tileData?.data?.event)
          isDevMode && console.log('navigate to', `/event/${tileData.id}`)

          if (shouldNavigateToEventUrl) {
            navigate(`/event/${tileData.id}`, {
              replace: true,
            })
          }
          return listOptions && setListPopup(null)
        }

        if (
          tileData?.type === tileTypes.CHANNEL_PLAYER ||
          tileData?.type === tileTypes.DATA_WIDGET ||
          tileData?.type === tileTypes.MEDIA_PLAYER
        ) {
          return
        }

        if (tileData.type === tileTypes.SHOW_MORE) {
          if (listContextId) {
            setListPopup({
              listContextId,
              isSearchListType,
              name,
              tileShape,
              searchResultType,
              activeTileIndex: tileIndex,
            })
            const path = `${paths.LIST}/${listContextId}/${name}`
            sendPageImpressionToGTAG(path)
            sendPageViewEvent(path)
          }
          return
        }

        if (tileData?.type === tileTypes.PAGE) {
          isDevMode && console.log('[useTileOpen] Open Tile ', tileData?.type)
          const pageId = tileData.data?.pageId

          if (!pageId) {
            return
          }

          const path = getPathByPageId(pageId)
          if (path) {
            navigate(path)
            closePagePopup()
            setCurrentEvent(null)
            setSubMenuType(null)
            return listOptions && setListPopup(null)
          }

          const pageOptions = {
            focusId,
            anchorId,
          }
          controller.current?.abort()
          controller.current = new AbortController()
          const abortSignal = controller.current.signal
          await getPagePopupById(pageId, {
            listOptions,
            pageOptions,
            abortSignal,
            skipLoadingStatus: true,
          })
          //TODO check on react 18 if https://b1smarttv-internal.atlassian.net/browse/SWD-226 reproduces without setTimeout
          return listOptions && setTimeout(setListPopup, 0, null)
        }

        if (tileData?.type === tileTypes.CHANNEL) {
          isDevMode && console.log('[useTileOpen] Open Tile ', tileData?.type)
          const channel = tileData?.data?.channel
          const video = channel?.video
          const videoSource = video?.sources?.[0]

          if (channel) {
            const tabIndex = pagePopup ? pagePopup.tabIndex : page?.tabIndex
            const videoWithName: MediaContentType = {
              contentType: tileData.type,
              name: channel.name,
              id: channel.id,
              listOptions,
              channel,
              redirectSearch: `?tabIndex=${tabIndex}&anchorId=${anchorId}&focusId=${focusId}`,
            }
            setMediaVideo(videoWithName)
            video &&
              videoSource &&
              sendAnalyticsEvent({
                object: eventObject.VIDEO,
                action: eventAction.START_CLICK,
                video: {
                  id: channel.id || tileData.id,
                  name: channel.name || tileData.title || tileData.id,
                  playerName: videoPlayerNames.HLS,
                  type: analyticsVideoTypes.STREAM,
                  link: videoSource.url,
                  partnerIds: channel.partnerIds,
                },
              })

            return listOptions && setListPopup(null)
          }

          return
        }

        if (
          tileData?.type === tileTypes.MEDIA ||
          tileData?.type === tileTypes.AD
        ) {
          isDevMode && console.log('[useTileOpen] Open Tile ', tileData?.type)
          isDevMode && console.log('[useTileOpen] Media: ', tileData)
          const mediaData = tileData?.data?.mediaData
          const video = mediaData?.video || tileData?.data?.media
          const videoSource = video?.sources?.[0]

          if (!mediaData) {
            isDevMode && showError('Error: Tile mediaData missing')
            isDevMode && console.log('Error: Tile mediaData missing')
            return
          }

          if (video && videoSource) {
            const videoWithName: MediaContentType = {
              contentType: tileData.type,
              name: tileData.title || video.id || tileData.id,
              id: video.id || tileData.id,
              sources: video.sources,
              onEnded: video.onEnded,
              listOptions,
              partnerIds: mediaData.partnerIds,
            }
            setMediaVideo(videoWithName)
            sendAnalyticsEvent({
              object: eventObject.VIDEO,
              action: eventAction.START_CLICK,
              video: {
                id: video.id || tileData.id,
                name: tileData.title || video.id || tileData.id,
                playerName: videoPlayerNames.HLS,
                type: analyticsVideoTypes.VOD,
                link: videoSource.url,
                partnerIds: mediaData.partnerIds,
              },
            })
            isDevMode &&
              !mediaData?.video &&
              showInfo(
                'Warning: the deprecated media object is used. Update mediaData',
              )
            return listOptions && setListPopup(null)
          } else {
            isDevMode && showError('Error: no video or videoSource found')
          }

          const image = mediaData?.image
          if (image?.sources?.[0]) {
            const imageWithName: MediaContentType = {
              contentType: tileData.type,
              name: tileData.title,
              id: image.id,
              sources: image.sources,
              isImageType: true,
              listOptions,
            }
            setMediaVideo(imageWithName || null)
            return listOptions && setListPopup(null)
          }

          return
        }

        if (tileData?.type === tileTypes.DEEP_LINK) {
          if (tileData.data?.deepLink?.tizen?.appId) {
            const deepLink = tileData.data.deepLink.tizen
            // app will show 'confirmOpenAppIdTitle' instead of actual translation if next string isn't executed
            t('confirmOpenAppIdTitle')
            t('anotherApp')
            t('confirmOpenAppIdDescription1')
            t('confirmOpenAppIdDescription2')
            useConfirmationStore.getState().confirm({
              title: `${t('confirmOpenAppIdTitle')} ${
                tileData.data?.deepLink?.tizen.appName || t('anotherApp')
              }`,
              description: `${t('confirmOpenAppIdDescription1')} ${
                tileData.title
              } ${t('confirmOpenAppIdDescription2')}`,
              prevFocusId: lineId ?? focusId,
              onSubmit: () => {
                deepLinkToAppId(deepLink)
                sendEventToGTAG(appOpenedSource.DEEP_LINK, {
                  app_name: tileData.data?.deepLink?.tizen?.appName,
                  category: tileData.data?.event?.categoryId,
                })
              },
            })
          }
          return
        }

        if (tileData.type === tileTypes.HTML_WIDGET) {
          const htmlWidget = tileData?.data?.htmlWidget

          if (htmlWidget?.url) {
            const htmlWidgetData: MediaContentType = {
              contentType: tileData.type,
              id: htmlWidget.url,
              htmlWidgetUrl: htmlWidget.url,
              isHtmlWidgetType: true,
              listOptions,
            }
            setMediaVideo(htmlWidgetData)
            listOptions && setListPopup(null)
          }
          return
        }
      } finally {
        setTileIsLoading(focusId, false)
      }
    },
    [shouldNavigateToEventUrl, navigate, t],
  )
}
